import type { DropDownNoSelection, DropDownResult, JsonData } from '@myosh/odin-components';
import { Button, DropDown, OdinIcon } from '@myosh/odin-components';
import { cloneDeep } from 'lodash';
import { forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useBowtieHazardMueMaterialUnwantedEventsJobPostMutation } from '../../api/enhanced/enhanced-bowtie-api';
import type { BowtieHazardMueMaterialUnwantedEventsPostApiResponse } from '../../api/generated/bowtie-api';
import { cn } from '../../helpers/util';
import { useGetAIBackgroundJobResult } from '../../hooks/use-get-ai-background-job-result';
import Accordion from './accordion.component';
import AddItemDialog from './add-item-dialog.component';
import './bowtie-wizard.styles.css';
import Dialog, { type DialogRef } from './dialog';

interface BowtieWizardProps {}

export interface BowtieWizardRef {
  open: () => void;
  close: () => void;
}

interface WizardDropDownItem {
  text: string;
  value: string;
  mue: Array<DropDownResult>;
}

interface BowtieWizardState {
  canGenerateMUEs: boolean;
  loading: boolean;
  hazardMuePair: Array<WizardDropDownItem>;
  selectedMUE?: DropDownResult;
  selectedHazard?: DropDownResult;
  isAddHazard: boolean;
}

const initialState: BowtieWizardState = {
  canGenerateMUEs: false,
  loading: false,
  hazardMuePair: [],
  selectedMUE: undefined,
  selectedHazard: undefined,
  isAddHazard: false,
};

const addNewActionItem = [{ text: 'Add New', value: 'add-new', selected: false }];

const BowtieWizard = (_props: BowtieWizardProps, ref: Ref<BowtieWizardRef>) => {
  const [state, setState] = useState<BowtieWizardState>(initialState);
  const [jobId, setJobId] = useState<string>();
  const { canGenerateMUEs, loading, hazardMuePair, selectedMUE, selectedHazard, isAddHazard } = state;

  const dialogRef = useRef<DialogRef>(null);
  const addDataDialogRef = useRef<DialogRef>(null);
  const termInputRef = useRef<HTMLInputElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const newItemInputRef = useRef<HTMLInputElement>(null);
  const errorTextRef = useRef<HTMLParagraphElement>(null);

  const [startMaterialUnwantedEventsJob] = useBowtieHazardMueMaterialUnwantedEventsJobPostMutation();

  const {
    data: backgroundJobResult,
    error: backgroundJobError,
    loading: backgroundJobLoading,
  } = useGetAIBackgroundJobResult({
    jobId: jobId!,
    setJobId: setJobId,
  });

  const isLoading = loading || backgroundJobLoading;

  useEffect(() => {
    if (backgroundJobResult && !isLoading && !backgroundJobError) {
      setJobId(undefined);

      const data = backgroundJobResult as unknown as BowtieHazardMueMaterialUnwantedEventsPostApiResponse;
      if (data?.length > 0) {
        if (isAddHazard) {
          const inputValue = newItemInputRef.current?.value?.trim();
          const [firstData] = data;

          const newHazardMuePair = [
            ...cloneDeep(hazardMuePair),
            {
              text: inputValue!,
              value: inputValue!,
              mue: mapMUE(firstData.material_unwanted_event),
            },
          ];

          const newSelectedHazard = {
            text: firstData.hazard,
            value: firstData.hazard,
            originalData: { mue: mapMUE(firstData.material_unwanted_event) },
          };

          setState((prevState) => ({
            ...prevState,
            loading: false,
            selectedMUE: undefined,
            hazardMuePair: newHazardMuePair,
            selectedHazard: newSelectedHazard,
            isAddHazard: false,
          }));

          if (newItemInputRef.current) newItemInputRef.current.value = '';
        } else {
          const hazardMuePair = data.map(({ material_unwanted_event, hazard }) => ({
            text: hazard,
            value: hazard,
            mue: mapMUE(material_unwanted_event),
          }));

          setState((state) => ({ ...state, hazardMuePair, loading: false }));
        }
      }
    } else if (backgroundJobError && !isLoading) {
      toast.error('Unable to generate hazards and unwanted events, please try again later.');
      setState((state) => ({ ...state, loading: false }));
    }
    setJobId(undefined);
  }, [backgroundJobResult, backgroundJobError, isLoading]);

  useImperativeHandle(ref, () => ({
    open: () => dialogRef.current?.open(),
    close,
  }));

  const close = useCallback(() => dialogRef.current?.close(), []);

  const handleTermInputChange = () => {
    if (!termInputRef.current?.value) {
      setState(initialState);
    }
  };

  const handleGenerateMUEs = () => {
    if (termInputRef.current?.value && termInputRef.current.value.trim().length > 0) {
      setState((state) => ({
        ...state,
        canGenerateMUEs: true,
        loading: true,
        selectedMUE: undefined,
        selectedHazard: undefined,
      }));

      startMaterialUnwantedEventsJob({
        hazardMueRequest: {
          term: termInputRef.current.value.trim(),
          extra_prompt: promptInputRef?.current?.value.trim(),
        },
      })
        .unwrap()
        .then((jobId) => {
          setJobId(jobId);
          setState((state) => ({ ...state, loading: false }));
        })
        .catch(() => {
          setJobId(undefined);
          toast.error('Unable to generate hazards and unwanted events, please try again later.');
          setState((state) => ({ ...state, loading: false }));
        });
    }
  };

  const handleMUESelectionChange = (
    materialUnwantedEvent?: DropDownResult | DropDownResult[] | DropDownNoSelection
  ) => {
    if (materialUnwantedEvent && !Array.isArray(materialUnwantedEvent)) {
      setState((state) => ({ ...state, selectedMUE: materialUnwantedEvent as DropDownResult }));
    }
  };

  const handleHazardSelectionChange = (hazard?: DropDownResult | DropDownResult[] | DropDownNoSelection) => {
    if (hazard && !Array.isArray(hazard)) {
      const newHazard = hazard as DropDownResult;
      if ('originalData' in hazard) {
        newHazard!.originalData!.mue = hazard?.originalData?.mue || [];
      }
      setState((state) => ({ ...state, selectedMUE: undefined, selectedHazard: newHazard as DropDownResult }));
    }
  };

  const handleGenerateBowtieClick = () => {
    if (
      termInputRef.current?.value &&
      termInputRef.current.value.trim().length > 0 &&
      selectedMUE?.value &&
      selectedHazard?.value
    ) {
      // open new page and load bowtie
      const params = new URLSearchParams({
        term: termInputRef.current.value.trim(),
        hazard: selectedHazard.value as string,
        mue: selectedMUE.value as string,
      });

      const promptValue = promptInputRef.current?.value ?? '';
      if (promptValue) {
        params.append('prompt', promptValue);
      }

      window.open(`${window.location.origin}${window.location.pathname}?${params}`);
    }
  };

  const handleSubmitAddItem = async () => {
    const inputValue = newItemInputRef.current?.value?.trim();

    if (inputValue && inputValue.length > 0) {
      if (inputValue.length > 100) {
        if (errorTextRef.current) {
          errorTextRef.current.style.display = 'block';
          errorTextRef.current.textContent = 'Value must not exceed 100 characters';
        }
      } else {
        if (errorTextRef.current) errorTextRef.current.style.display = 'none';

        if (isAddHazard) {
          setState((state) => ({
            ...state,
            loading: true,
          }));

          startMaterialUnwantedEventsJob({
            hazardMueRequest: {
              term: termInputRef?.current?.value.trim() as string,
              extra_prompt: promptInputRef?.current?.value.trim(),
              hazard: inputValue,
            },
          })
            .unwrap()
            .then((jobId) => {
              setJobId(jobId);
              setState((state) => ({ ...state, loading: false }));
            })
            .catch(() => {
              toast.error('Unable to generate hazards and unwanted events, please try again later.');
              setJobId(undefined);
              setState((state) => ({ ...state, loading: false }));
            });
        } else {
          const newSelectedHazard = { ...selectedHazard };

          (newSelectedHazard?.originalData?.mue as { text: string; value: string }[]).push({
            text: inputValue,
            value: inputValue,
          });

          const newHazardMuePair = hazardMuePair.map((item) => {
            if (item.value === selectedHazard?.value) {
              return {
                ...item,
                mue: newSelectedHazard?.originalData?.mue as DropDownResult[],
              };
            }
            return item;
          });

          setState((prevState) => ({
            ...prevState,
            hazardMuePair: newHazardMuePair,
            selectedHazard: newSelectedHazard as DropDownResult,
            selectedMUE: {
              value: inputValue,
              text: inputValue,
              originalData: {
                value: inputValue,
                text: inputValue,
              },
            },
          }));
          if (newItemInputRef.current) newItemInputRef.current.value = '';
        }

        addDataDialogRef.current?.close();
      }
    } else {
      if (errorTextRef.current) {
        errorTextRef.current.style.display = 'block';
        errorTextRef.current.textContent = 'Must enter a value';
      }
    }
  };

  const handleShowHazardAddItemDialog = () => {
    setState((state) => ({ ...state, isAddHazard: true }));
    addDataDialogRef.current?.open();
  };

  const handleShowMueAddItemDialog = () => {
    setState((state) => ({ ...state, isAddHazard: false }));
    addDataDialogRef.current?.open();
  };

  const sectionStyles = 'hide-checkbox-container flex flex-1 flex-col items-center gap-4 rounded-md bg-gray-5 p-4';

  const HazardDropDownSelectorStyles = cn('text-sm', {
    "before:content-['Select_Hazard'] before:text-gray-3": !selectedHazard,
  });

  const MUEDropDownSelectorStyles = cn('text-sm', {
    "before:content-['Select_Unwanted_Event'] before:text-gray-3": !selectedMUE,
  });

  return (
    <>
      <Dialog
        data-cy="bowtie-wizard-dialog"
        className="text-gray-1 m-auto w-[600px] rounded-md p-4 outline-none"
        ref={dialogRef}
        onCancel={close}
      >
        <div className="mb-4 flex flex-row items-center justify-between">
          <div className="text-xl font-medium">Bowtie AI Wizard</div>
          <div data-cy="bowtie-wizard-close" title="Close" className="text-primary-2 cursor-pointer" onClick={close}>
            <OdinIcon icon="X" />
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className={sectionStyles}>
            <div className="w-full">
              <label htmlFor="term-input" className="flex self-start text-sm font-bold">
                Describe an area or term to generate Unwanted Events for
              </label>
              <input
                data-cy="bowtie-wizard-term-input"
                ref={termInputRef}
                name="term-input"
                onChange={handleTermInputChange}
                className="border-gray-4 flex w-full flex-1 appearance-none border px-2 py-1 text-sm focus:outline-none"
              />
            </div>
            <Button
              data-cy="bowtie-wizard-generate-button"
              variant="alternative"
              onClick={handleGenerateMUEs}
              disabled={canGenerateMUEs && isLoading}
              style={{ minWidth: '250px' }}
            >
              {canGenerateMUEs && isLoading ? 'Generating...' : 'Generate Hazards and Events'}
            </Button>
          </div>
          <div className={sectionStyles}>
            <DropDown
              data-cy="bowtie-wizard-hazard-dropdown"
              className={cn({
                'pointer-events-none opacity-50': !canGenerateMUEs || isLoading,
              })}
              label="Hazards"
              data={hazardMuePair as unknown as JsonData}
              textField="text"
              valueField="value"
              allowClear={false}
              allowSearch={true}
              onChange={handleHazardSelectionChange}
              value={selectedHazard}
              selectorStyles={HazardDropDownSelectorStyles}
              actionItems={addNewActionItem}
              onActionItemChanged={handleShowHazardAddItemDialog}
            />
            <DropDown
              data-cy="bowtie-wizard-mue-dropdown"
              className={cn({
                'pointer-events-none opacity-50': !canGenerateMUEs || isLoading || !selectedHazard?.value,
              })}
              label="Unwanted Events"
              data={selectedHazard?.originalData?.mue as unknown as JsonData}
              textField="text"
              valueField="value"
              allowClear={false}
              allowSearch={true}
              onChange={handleMUESelectionChange}
              value={selectedMUE}
              selectorStyles={MUEDropDownSelectorStyles}
              actionItems={addNewActionItem}
              onActionItemChanged={handleShowMueAddItemDialog}
            />
            <Accordion title="Advanced">
              <div className="w-full">
                <label htmlFor="custom-instructions" className="flex self-start text-sm font-bold">
                  Custom Instructions
                </label>
                <textarea
                  data-cy="bowtie-wizard-prompt-textarea"
                  ref={promptInputRef}
                  name="custom-instructions"
                  placeholder="Provide extra detail about your situation to generate a more relevant response"
                  className="border-gray-4 flex w-full flex-1 resize-none appearance-none border px-2 py-1 text-sm focus:outline-none"
                  cols={30}
                  rows={5}
                />
              </div>
            </Accordion>
            <Button
              data-cy="bowtie-wizard-submit-button"
              variant="alternative"
              type="primary"
              onClick={handleGenerateBowtieClick}
              disabled={!selectedMUE || !selectedHazard}
              style={{ maxWidth: '200px' }}
            >
              Generate Bowtie
            </Button>
            <p className="text-warning self-start text-xs">
              Warning: Data is sent to an external AI. Verify all AI results for accuracy.
            </p>
          </div>
        </div>
      </Dialog>
      <AddItemDialog
        ref={addDataDialogRef}
        errorTextRef={errorTextRef}
        inputRef={newItemInputRef}
        onSubmit={handleSubmitAddItem}
        disabled={isLoading}
      />
    </>
  );
};

const mapMUE = (mues: string[]) => mues.map((mue) => ({ text: mue, value: mue }));

export default forwardRef(BowtieWizard);
